import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateRegistrationData } from '../../store/userSlice';

import LocationSelect from '../../components/LocationSelect';
import DatePicker from '../../components/DatePicker';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import DotSpinner from '../../components/shared/DotSpinner';

function Main() {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userId } = useSelector((state) => state.auth);
  const userData = useSelector((state) => state.user.userData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    gender: null,
    birthDate: '',
    country: '',
    city: '',
    latitude: null,
    longitude: null,
  });

  const genders = [
    { id: 'male', label: 'Мужчина', emoji: '👱🏻‍♂️' },
    { id: 'female', label: 'Женщина', emoji: '👱🏻‍♀️' },
  ];


  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    if (!userId) return;
    try {
      const response = await axiosPrivate.get(`/users/${userId}`).then((res) => res.data);
      console.log('fetchUserData', response);
      const formattedBirthDay = response.birthDay ? new Date(response.birthDay).toISOString().split('T')[0] : '';

      setFormData({
        name: response.name || '',
        gender: response.gender || null,
        birthDate: response.birthDay || '',
        country: response.country || '',
        city: response.city || '',
        latitude: response.coordinates?.latitude || null,
        longitude: response.coordinates?.longitude || null,
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleLocationSelect = (locationData) => {
    setFormData((prev) => ({
      ...prev,
      country: locationData.country,
      city: locationData.city,
      latitude: locationData.coordinates.latitude,
      longitude: locationData.coordinates.longitude,
    }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      birthDate: date,
    }));
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);

      const requestData = {
        telegramId: userId,
        name: formData.name,
        gender: formData.gender,
        birthDay: formData.birthDate, // This will be in yyyy-MM-dd format
        country: formData.country,
        city: formData.city,
        latitude: formData.latitude,
        longitude: formData.longitude,
      };

      const response = await axiosPrivate.put('/users/mainInfoUpdate', requestData);

      if (response.data) {
        dispatch(
          updateRegistrationData({
            name: formData.name,
            gender: formData.gender,
            birthDay: formData.birthDate,
            country: formData.country,
            city: formData.city,
            coordinates: {
              latitude: formData.latitude,
              longitude: formData.longitude,
            },
          }),
        );

        navigate(-1);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Произошла ошибка при сохранении данных. Пожалуйста, попробуйте еще раз.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[100vh]">
        <DotSpinner />
      </div>
    );
  }

  return (
    <div className="w-[100vw] flex items-center justify-center flex-col">
      <div className="sticky top-0 left-0 right-0 z-50 pt-[26px] pb-[30px] w-full pr-[16px] pl-[16px] flex-row text-[18px] flex justify-between">
        <span
          className="text-white"
          onClick={() => {
            navigate(-1);
          }}
        >
          Отмена
        </span>
        <span
          className={`${isSubmitting ? 'opacity-50' : 'text-[#A1F69E]'}`}
          onClick={isSubmitting ? undefined : handleSave}
        >
          {isSubmitting ? 'Сохранение...' : 'Сохранить'}
        </span>
      </div>
      <div className="flex flex-col justify-start items-start w-full p-4">
        <h1 className="font-raleway font-semibold text-white text-[20px]">Как вас зовут?</h1>
        <input
          type="text"
          name="name"
          placeholder="Ваше имя"
          className="w-full h-[64px] rounded-[8px] bg-[#022424] mt-4 pl-4 border border-[#233636] text-white outline-none focus:border-[#a1f69e]"
          value={formData.name}
          onChange={handleChange}
          maxLength={40}
        />
        <h1 className="font-raleway font-semibold mt-[32px] text-white text-[20px]">Ваш пол</h1>
        <div className="flex justify-start flex-wrap items-center gap-[16px] mt-[16px]">
          {genders.map((gender) => (
            <div
              key={gender.id}
              onClick={() => setFormData((prev) => ({ ...prev, gender: gender.id }))}
              className={`w-[145px] h-[48px] rounded-[400px] flex justify-center items-center text-[18px] text-white gap-[8px] cursor-pointer transition-all 
                ${
                  formData.gender === gender.id
                    ? 'bg-[#043939] border-[1.5px] border-[#a1f69e]'
                    : 'bg-[#022424] border border-transparent'
                }`}
            >
              <span className="text-[24px]">{gender.emoji}</span>
              <span>{gender.label}</span>
            </div>
          ))}
        </div>


        <h1 className="font-raleway font-semibold mt-[32px] text-white text-[20px]">Ваша дата рождения?</h1>
        <div className="w-full h-[64px] flex justify-center items-center rounded-[8px] bg-[#022424] mt-4 pl-4 border border-[#233636] text-white outline-none focus:border-[#a1f69e]">
          <DatePicker value={formData.birthDate} onChange={handleDateChange} />
        </div>

        <LocationSelect
          onLocationSelect={handleLocationSelect}
          initialCountry={formData.country}
          initialCity={formData.city}
        />
      </div>
    </div>
  );
}

export default Main;
